/**
 * Platform-agnostic authentication manager
 *
 * Uses storage adapter pattern for cross-platform compatibility.
 * Configurable key prefix for multi-tenant or multi-app environments.
 */
export class AuthManager {
  /**
   * @param {Object} storage - Storage adapter implementing get/set/remove
   * @param {Object} [options] - Configuration options
   * @param {string} [options.keyPrefix='realm_'] - Prefix for storage keys
   */
  constructor(storage, options = {}) {
    if (!storage) {
      throw new Error('AuthManager requires a storage adapter')
    }
    this.storage = storage
    this.client = null
    this.refreshPromise = null
    this.keyPrefix = options.keyPrefix || 'realm_'
  }

  /**
   * Get storage key with prefix
   * @private
   */
  _getKey(key) {
    return `${this.keyPrefix}${key}`
  }

  /**
   * Set RPC client for refresh token operations
   * @param {Object} client - Workers-channel client instance
   */
  setClient(client) {
    this.client = client
  }

  /**
   * Get access token from storage
   * @returns {Promise<string|null>}
   */
  async getAccessToken() {
    return await this.storage.get(this._getKey('access_token'))
  }

  /**
   * Store access token
   * @param {string} token - JWT access token
   * @returns {Promise<void>}
   */
  async setAccessToken(token) {
    if (!token) throw new Error('Token is required')
    await this.storage.set(this._getKey('access_token'), token)
  }

  /**
   * Clear access token from storage (best-effort cleanup)
   * @returns {Promise<void>}
   */
  async clearAccessToken() {
    try {
      await this.storage.remove(this._getKey('access_token'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear access token:', error)
    }
  }

  /**
   * Get refresh token from storage
   * @returns {Promise<string|null>}
   */
  async getRefreshToken() {
    return await this.storage.get(this._getKey('refresh_token'))
  }

  /**
   * Store refresh token
   * @param {string} token - JWT refresh token
   * @returns {Promise<void>}
   */
  async setRefreshToken(token) {
    if (!token) throw new Error('Token is required')
    await this.storage.set(this._getKey('refresh_token'), token)
  }

  /**
   * Clear refresh token from storage (best-effort cleanup)
   * @returns {Promise<void>}
   */
  async clearRefreshToken() {
    try {
      await this.storage.remove(this._getKey('refresh_token'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear refresh token:', error)
    }
  }

  /**
   * Store both access and refresh tokens
   * @param {string} accessToken
   * @param {string} refreshToken
   * @returns {Promise<void>}
   */
  async setTokens(accessToken, refreshToken) {
    await this.setAccessToken(accessToken)
    if (refreshToken) {
      await this.setRefreshToken(refreshToken)
    }
  }

  /**
   * Clear all auth tokens
   * @returns {Promise<void>}
   */
  async clearTokens() {
    await Promise.all([this.clearAccessToken(), this.clearRefreshToken()])
  }

  /**
   * Decode JWT token payload
   * @param {string} token - JWT token
   * @returns {Object|null} Decoded payload or null if invalid
   */
  decodeJWT(token) {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded)
    } catch (error) {
      console.error('[AuthManager] Failed to decode JWT:', error)
      return null
    }
  }

  /**
   * Check if token is expired
   * @param {string} token - JWT token
   * @returns {boolean} True if expired
   */
  isTokenExpired(token) {
    const payload = this.decodeJWT(token)
    if (!payload || !payload.exp) {
      return true
    }

    const now = Math.floor(Date.now() / 1000)
    return now >= payload.exp
  }

  /**
   * Refresh access token using refresh token
   * @param {string} workerName - Name of auth worker (default: 'user-auth-worker')
   * @returns {Promise<string|null>} New access token or null if refresh failed
   * @private
   */
  async _refreshAccessToken(workerName = 'user-auth-worker') {
    if (!this.client) {
      return null
    }

    const refreshToken = await this.getRefreshToken()
    if (!refreshToken) {
      return null
    }

    if (this.isTokenExpired(refreshToken)) {
      await this.clearTokens()
      return null
    }

    try {
      const response = await this.client.callByName(workerName, 'refreshAccessToken', [
        { refreshToken },
      ])

      if (!response.accessToken) {
        return null
      }

      await this.setAccessToken(response.accessToken)
      // Save rotated refresh token if server issued a new one
      if (response.refreshToken) {
        await this.setRefreshToken(response.refreshToken)
      }
      return response.accessToken
    } catch (error) {
      // Only clear tokens for auth rejection (401). Transient errors keep tokens intact.
      if (error?.code === 401) {
        await this.clearTokens()
      }
      return null
    }
  }

  /**
   * Get valid access token (auto-refreshes if expired)
   * @param {string} [workerName='user-auth-worker'] - Name of auth worker
   * @returns {Promise<string|null>}
   */
  async getValidAccessToken(workerName = 'user-auth-worker') {
    const token = await this.getAccessToken()

    if (!token) {
      return null
    }

    if (!this.isTokenExpired(token)) {
      return token
    }

    if (this.refreshPromise) {
      return await this.refreshPromise
    }

    this.refreshPromise = this._refreshAccessToken(workerName)

    try {
      const newToken = await this.refreshPromise
      return newToken
    } finally {
      this.refreshPromise = null
    }
  }

  /**
   * Check if user is logged in (has valid or refreshable tokens)
   * @returns {Promise<boolean>}
   */
  async isLoggedIn() {
    const token = await this.getValidAccessToken()
    if (token) return true
    // Even if getValidAccessToken failed (e.g. transient network error),
    // the user is still logged in if we have a non-expired refresh token
    const refreshToken = await this.getRefreshToken()
    return !!refreshToken && !this.isTokenExpired(refreshToken)
  }

  /**
   * Get user info from access token
   * @returns {Promise<Object|null>} User info or null
   */
  async getUserInfo() {
    const token = await this.getValidAccessToken()
    if (!token) {
      return null
    }

    const payload = this.decodeJWT(token)
    if (!payload) {
      return null
    }

    return {
      sub: payload.sub,
      provider: payload.sub_type || payload.provider,
      identifier: payload.identifier,
      email: payload.email || payload.identifier,
      name: payload.name || payload.identifier?.split('@')[0],
      picture: payload.picture,
      exp: payload.exp,
      iat: payload.iat,
    }
  }

  /**
   * Get login method from storage
   * @returns {Promise<string|null>}
   */
  async getLoginMethod() {
    return await this.storage.get(this._getKey('login_method'))
  }

  /**
   * Set login method
   * @param {string} method - Login method (e.g., 'google', 'eth')
   * @returns {Promise<void>}
   */
  async setLoginMethod(method) {
    await this.storage.set(this._getKey('login_method'), method)
  }

  /**
   * Clear login method (best-effort cleanup)
   * @returns {Promise<void>}
   */
  async clearLoginMethod() {
    try {
      await this.storage.remove(this._getKey('login_method'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear login method:', error)
    }
  }

  /**
   * Get logged in flag
   * @returns {Promise<boolean>}
   */
  async getLoggedInFlag() {
    const flag = await this.storage.get(this._getKey('logged_in'))
    return flag === 'true' || flag === true
  }

  /**
   * Set logged in flag
   * @param {boolean} value
   * @returns {Promise<void>}
   */
  async setLoggedInFlag(value) {
    await this.storage.set(this._getKey('logged_in'), value ? 'true' : 'false')
  }

  /**
   * Clear logged in flag (best-effort cleanup)
   * @returns {Promise<void>}
   */
  async clearLoggedInFlag() {
    try {
      await this.storage.remove(this._getKey('logged_in'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear logged in flag:', error)
    }
  }

  /**
   * Get all storage keys used by AuthManager
   * @returns {string[]}
   */
  getStorageKeys() {
    return [
      this._getKey('access_token'),
      this._getKey('refresh_token'),
      this._getKey('login_method'),
      this._getKey('logged_in'),
    ]
  }

  /**
   * Complete logout - clear all auth data
   * @returns {Promise<void>}
   */
  async logout() {
    await Promise.all([this.clearTokens(), this.clearLoginMethod(), this.clearLoggedInFlag()])
  }
}
